import { useState } from 'react';
import { Box, Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { createEmployeeFaltasApi } from '../faltas/api';
import { faltasGlobalApi } from './api';
import { FaltaGlobalFormDialog } from './FaltaGlobalFormDialog';
import type { FaltaGlobalListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataFalta',
  sortOrder: 'desc',
};

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * Visão global de Faltas (administrativo) — grid de todos os colaboradores com
 * filtro por colaborador + CRUD. Espelha a página Projetos global. A listagem
 * usa `faltasGlobalApi` (mock até card [YuqtX0mn]); as mutações reusam o
 * sub-recurso aninhado `/employees/:id/faltas`.
 */
export function FaltasGlobalPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('faltas-global', DEFAULT_QUERY, {
    colaboradorCodigo: null as number | null,
  });
  const colabFiltro = filters.colaboradorCodigo;
  const setColabFiltro = (id: number | null) => patchFilters({ colaboradorCodigo: id });
  const [editing, setEditing] = useState<FaltaGlobalListItem | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<FaltaGlobalListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['faltas', 'global', query, colabFiltro],
    queryFn: () => faltasGlobalApi.list(query, { colaboradorCodigo: colabFiltro }),
    placeholderData: (previous) => previous,
  });

  const deleteMut = useMutation({
    mutationFn: (row: FaltaGlobalListItem) =>
      createEmployeeFaltasApi(row.employeeId).remove(row.dataFalta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['faltas', 'global'] });
      toast.success(t('features.colaboradores.faltas.toasts.deleted'));
      setPendingDelete(null);
    },
    onError: (err) => toast.error(describeApiError(err)),
  });

  const columns: GridColDef<FaltaGlobalListItem>[] = [
    {
      field: 'colaboradorNome',
      headerName: t('features.colaboradores.faltasGlobal.columns.colaborador'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'dataFalta',
      headerName: t('features.colaboradores.faltas.columns.dataFalta'),
      width: 140,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'justificada',
      headerName: t('features.colaboradores.faltas.columns.justificada'),
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.row.justificada
              ? t('features.colaboradores.faltas.justificada.yes')
              : t('features.colaboradores.faltas.justificada.no')
          }
          color={params.row.justificada ? 'success' : 'default'}
          variant={params.row.justificada ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'motivo',
      headerName: t('features.colaboradores.faltas.columns.motivo'),
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.faltasGlobal.actions.edit', {
                nome: params.row.colaboradorNome,
                data: formatDate(params.row.dataFalta),
              })}
              onClick={() => setEditing(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.colaboradores.faltasGlobal.actions.delete', {
                nome: params.row.colaboradorNome,
                data: formatDate(params.row.dataFalta),
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={t('features.colaboradores.faltasGlobal.title')}
        subtitle={t('features.colaboradores.faltasGlobal.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.colaboradores.faltasGlobal.actions.new')}
          </Button>
        }
      />
      <Stack spacing={2}>
        {/* Erro na carga inicial: mostra o erro DENTRO da página — cabeçalho
            preservado + retry — em vez de substituir a tela inteira. Espelha
            a FeriasGlobalPage. */}
        {listQuery.isLoadingError ? (
          <ErrorState
            description={describeApiError(listQuery.error)}
            onRetry={() => listQuery.refetch()}
          />
        ) : (
          <DataTable<FaltaGlobalListItem>
            rows={listQuery.data?.data ?? []}
            columns={columns}
            total={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isLoading || listQuery.isFetching}
            query={query}
            onQueryChange={setQuery}
            onClear={clear}
            searchPlaceholder={t('features.colaboradores.faltasGlobal.searchPlaceholder')}
            filters={
              <Box sx={{ minWidth: 220, flex: '1 1 260px' }}>
                <ColaboradorSelect
                  value={colabFiltro}
                  onChange={(id) => setColabFiltro(id)}
                  label={t('features.colaboradores.faltasGlobal.filters.colaborador')}
                  placeholder={t('features.colaboradores.faltasGlobal.filters.colaboradorTodos')}
                />
              </Box>
            }
          />
        )}
      </Stack>

      <FaltaGlobalFormDialog
        open={editing !== null}
        falta={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.faltas.deleteConfirm.title')}
        description={t('features.colaboradores.faltasGlobal.deleteConfirm.description', {
          nome: pendingDelete?.colaboradorNome ?? '',
          data: pendingDelete ? formatDate(pendingDelete.dataFalta) : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMut.mutate(pendingDelete);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
}
